import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import classnames from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';

import styles from './styles.module.scss';
import Button from '../Button/index.ts';
import { useFormValidation } from '../Form/index.js';
import addVariants from '../utils/addVariants/index.ts';
import { useQuickValue } from '../utils/index.ts';
import useBoolean from '../utils/useBoolean/index.ts';
import useEventCallback from '../utils/useEventCallback/index.ts';
import useOverriddenState from '../utils/useOverriddenState/index.ts';

export const VARIANTS = ['default', 'wrap', 'dark', 'light'];
const noop = () => 0;
const ICONS = {
  CLOSE: faTimes
};

function InputFactory(Component) {
  const InnerInput = ({
    isDisabled,
    rules,
    shouldValidateOnBlur,
    hasError: hasErrorFromProps,
    value: currentValue,
    initialValue,
    onBlur: onBlurHandler,
    onFocus: onFocusHandler,
    onChangeFocus: onChangeFocusHandler,
    onChange: onChangeValue,
    onClear: onClearHandler,
    isClearable,
    isRequired,
    forwardedRef,
    ...props
  }) => {
    const [valueHasBeenEdited, setHasBeenEdited] = useState(false);
    const checkErrors = useCallback(
      newValue => {
        let errors = [];

        if (isRequired && !newValue) {
          errors = ['Value is required'];
        } else if (Array.isArray(rules)) {
          errors = rules.map(rule => rule(newValue)).filter(Boolean);
        }

        return errors;
      },
      [rules, isRequired]
    );
    const [value, setValue] = useOverriddenState(initialValue, currentValue, {
      onChange: v => {
        setHasBeenEdited(true);

        onChangeValue(v, { errors: checkErrors(v) });
      }
    });
    const errors = checkErrors(value);
    const currentlyHasErrors = errors.length;

    useFormValidation(!currentlyHasErrors);

    const onChange = useEventCallback(({ target: { value: newValue } }) => setValue(newValue));
    const {
      value: isFocused,
      setFalse: blur,
      setTrue: focus
    } = useBoolean(false, {
      onChange: onChangeFocusHandler
    });
    const onBlur = useEventCallback(e => {
      onBlurHandler(e);
      blur();
    });
    const onFocus = useEventCallback(e => {
      onFocusHandler(e);
      focus();
    });
    const onClear = useEventCallback(onClearHandler);
    const clearEl = useMemo(
      () =>
        isClearable ? (
          <Button
            variant={['flat', 'round']}
            color="hover-darken"
            icon={ICONS.CLOSE}
            className={classnames(addVariants('TextInput__body__main__clear', styles))}
            onMouseDown={e => {
              // Use onMouseDown because https://stackoverflow.com/a/43627784
              e.preventDefault();
              setValue('');
              onClear();
            }}
          />
        ) : null,
      [isClearable, setValue, onClear]
    );
    let hasErrors = false;
    if (
      hasErrorFromProps ||
      (currentlyHasErrors && value) ||
      (!value && isRequired && valueHasBeenEdited)
    ) {
      hasErrors = shouldValidateOnBlur ? !isFocused : true;
    }

    const [quickValue, quickOnChange] = useQuickValue(value, onChange);

    return (
      <Component
        error={errors}
        hasError={isDisabled ? false : hasErrors}
        value={quickValue}
        onBlur={isDisabled ? noop : onBlur}
        onFocus={isDisabled ? noop : onFocus}
        onChange={isDisabled ? noop : quickOnChange}
        isFocused={isDisabled ? false : isFocused}
        clearButton={value && clearEl}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isRequired={isRequired}
        ref={forwardedRef}
        {...props}
      />
    );
  };

  return InnerInput;
}

export default InputFactory;
